import React, { useContext, useMemo, useState } from "react";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import { InlineSpinner } from "components/Spinner";
import { LoggedInUserContext } from "../../contexts/LoggedInUser";

const ACCOUNT_API_NAME = "AccountGatewayApi";

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function formatDateTime(value) {
  if (!value) return "—";

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function getRoleList(profile) {
  const roles = [];

  safeArray(profile?.accounts).forEach((account) => {
    const role = normalizeText(account?.role);
    if (role) {
      roles.push(role);
    }
  });

  return Array.from(new Set(roles));
}

function canManageMfaRecovery(profile) {
  const roles = getRoleList(profile).map((role) => role.toLowerCase());

  return roles.includes("globaladmin");
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getUserEmail(item) {
  return normalizeText(item?.email);
}

function getUserName(item) {
  return normalizeText(item?.name);
}

function extractApiErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage
  );
}

function buildTotpResetUrl(email, token) {
  const normalizedEmail = normalizeText(email);
  const normalizedToken = normalizeText(token);

  if (!normalizedEmail || !normalizedToken) return "";

  const params = new URLSearchParams();
  params.set("email", normalizedEmail);
  params.set("token", normalizedToken);

  return `${window.location.origin}/auth/mfa/reset-authenticator?${params.toString()}`;
}

export default function MfaRecoveryAdminPage() {
  const { loggedInUserProfile } = useContext(LoggedInUserContext) || {};

  const [searchValue, setSearchValue] = useState("");
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userNotFound, setUserNotFound] = useState(false);
  const [submittingApprove, setSubmittingApprove] = useState(false);
  const [latestActionResult, setLatestActionResult] = useState(null);
  const [latestRecoveryToken, setLatestRecoveryToken] = useState("");

  const isAllowed = useMemo(() => {
    return canManageMfaRecovery(loggedInUserProfile);
  }, [loggedInUserProfile]);

  const totpResetUrl = useMemo(() => {
    return buildTotpResetUrl(
      getUserEmail(selectedUser),
      latestRecoveryToken
    );
  }, [latestRecoveryToken, selectedUser]);

  function selectUser(user) {
    setSelectedUser(user);
    setLatestActionResult(null);
    setLatestRecoveryToken("");
    setUserNotFound(false);
  }

  async function runSearch(event) {
    event.preventDefault();

    const searchedEmail = normalizeText(searchValue);
    if (!searchedEmail) {
      toast("Enter a user email to search.", {
        type: toast.TYPE.WARNING,
        autoClose: 4000,
      });
      return;
    }

    if (!isValidEmail(searchedEmail)) {
      toast("Please enter a valid email address.", {
        type: toast.TYPE.WARNING,
        autoClose: 4000,
      });
      return;
    }

    setSearching(true);
    setSelectedUser(null);
    setLatestActionResult(null);
    setLatestRecoveryToken("");
    setUserNotFound(false);

    try {
      const results = await API.get(
        ACCOUNT_API_NAME,
        `/profiles?email=${encodeURIComponent(searchedEmail)}`,
        { withCredentials: true }
      );

      const items = Array.isArray(results?.items) ? results.items : [];
      const normalizedEmail = searchedEmail.toLowerCase();
      const selectedMatch = items.find(
        (item) => getUserEmail(item).toLowerCase() === normalizedEmail
      );

      if (!selectedMatch) {
        setUserNotFound(true);
        toast("User does not exist.", {
          type: toast.TYPE.INFO,
          autoClose: 4000,
        });
        return;
      }

      selectUser(selectedMatch);
    } catch (error) {
      console.error("Error searching users for MFA recovery", error);
      toast("There was a problem searching users.", {
        type: toast.TYPE.ERROR,
        autoClose: 5000,
      });
    } finally {
      setSearching(false);
    }
  }

  async function approveRecovery() {
    const email = getUserEmail(selectedUser);
    if (!email) {
      toast("Please search for a valid user first.", {
        type: toast.TYPE.WARNING,
        autoClose: 4000,
      });
      return;
    }

    setSubmittingApprove(true);

    try {
      const result = await API.post(
        ACCOUNT_API_NAME,
        "/auth/approve-mfa-recovery",
        {
          body: { email },
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const recoveryToken = normalizeText(result?.recoveryToken);
      setLatestRecoveryToken(recoveryToken);
      setLatestActionResult({
        at: normalizeText(result?.approvedAt) || new Date().toISOString(),
      });

      toast("Authenticator app reset approved successfully.", {
        type: toast.TYPE.SUCCESS,
        autoClose: 5000,
      });

      if (!recoveryToken) {
        toast(
          "MFA reset was approved, but the recovery token was not returned.",
          {
            type: toast.TYPE.WARNING,
            autoClose: 6000,
          }
        );
      }
    } catch (error) {
      console.error("Error approving MFA recovery", error);
      toast(
        extractApiErrorMessage(
          error,
          "There was a problem approving authenticator app reset."
        ),
        {
          type: toast.TYPE.ERROR,
          autoClose: 5000,
        }
      );
    } finally {
      setSubmittingApprove(false);
    }
  }

  async function copyTotpResetLink() {
    if (!totpResetUrl) return;

    try {
      await navigator.clipboard.writeText(totpResetUrl);
      toast("Authenticator reset page link copied.", {
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });
    } catch {
      toast("Could not copy the authenticator reset page link.", {
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    }
  }

  if (!isAllowed) {
    return (
      <div style={{ padding: "1.25rem", fontSize: "1rem" }}>
        <div style={{ maxWidth: "75rem" }}>
          <h4
            style={{
              fontWeight: 700,
              marginBottom: "0.25rem",
              fontSize: "1.55rem"
            }}
          >
            MFA Recovery Admin
          </h4>
          <div
            style={{
              color: "#4B5563",
              marginBottom: "1rem",
              fontSize: "1rem"
            }}
          >
            View and manage account
          </div>
          <Alert variant="warning" className="mb-0">
            You do not have permission to access the MFA Recovery Admin page.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "1.25rem", fontSize: "1rem" }}>
      <div
        style={{
          fontSize: "1rem",
          color: "#6B7280",
          marginBottom: "1rem"
        }}
      >
        My Account&nbsp;&nbsp;&gt;&nbsp;&nbsp;Account
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <h4 style={{ fontWeight: 700, margin: 0, fontSize: "1.55rem" }}>
          MFA Recovery Admin
        </h4>
        <div style={{ color: "#4B5563", fontSize: "1rem" }}>
          View and manage account
        </div>
      </div>

      <div
        style={{
          border: "1px solid #D1D5DB",
          borderRadius: "1rem",
          background: "#FFFFFF",
          padding: "1.4rem",
          minHeight: "32rem",
        }}
      >
        <div
          style={{
            border: "1px solid #E5E7EB",
            borderRadius: "0.75rem",
            background: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "1.25rem 1.25rem 1.4rem" }}>
            <Form onSubmit={runSearch}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  maxWidth: "40rem"
                }}
              >
                <Form.Control
                  type="email"
                  required
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search user by official email address"
                  style={{
                    height: "2.7rem",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    fontSize: "1rem",
                  }}
                />
                <Button
                  variant="light"
                  type="submit"
                  className="btn"
                  style={{
                    height: "2.7rem",
                    width: "3.25rem",
                    border: "1px solid #CED4DA",
                    borderLeft: "none",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: "0.45rem",
                    borderBottomRightRadius: "0.45rem",
                    background: "#E5E7EB",
                    color: "#9CA3AF",
                    fontSize: "1rem",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  disabled={searching}
                  title="Search"
                >
                  {searching ? (
                    <InlineSpinner />
                  ) : (
                    <svg
                      width="1.45rem"
                      height="1.45rem"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <circle
                        cx="10.5"
                        cy="10.5"
                        r="6.5"
                        stroke="#9CA3AF"
                        strokeWidth="2.6"
                      />
                      <path
                        d="M15.5 15.5L20 20"
                        stroke="#9CA3AF"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </Button>
              </div>
            </Form>
          </div>

          {(selectedUser || userNotFound || searching) && (
            <div
              style={{
                borderTop: "1px solid #E5E7EB",
                padding: "1.25rem"
              }}
            >
              {searching && (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <InlineSpinner />
                </div>
              )}

              {!searching && userNotFound && (
                <div
                  style={{
                    maxWidth: "100%",
                    border: "1px solid #CBD5E1",
                    borderRadius: "0.55rem",
                    background: "#FFFFFF",
                    fontSize: "1rem",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      background: "#F8FAFC",
                      borderBottom: "1px solid #CBD5E1",
                      padding: "0.85rem 1.5rem",
                      color: "#005EB8",
                      fontWeight: 700,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.65rem",
                    }}
                  >
                    <svg
                      width="1.6rem"
                      height="1.6rem"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      style={{ flex: "0 0 auto" }}
                    >
                      <circle cx="12" cy="12" r="10" fill="#005EB8" />
                      <circle cx="12" cy="9" r="3" fill="#FFFFFF" />
                      <path
                        d="M6.8 18.2C7.6 15.8 9.55 14.3 12 14.3C14.45 14.3 16.4 15.8 17.2 18.2"
                        stroke="#FFFFFF"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                      />
                    </svg>
                    User Details
                  </div>

                  <div
                    style={{
                      padding: "2.25rem 2rem",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: "4.5rem",
                        height: "4.5rem",
                        margin: "0 auto 1.35rem",
                      }}
                    >
                      <div
                        style={{
                          width: "4.5rem",
                          height: "4.5rem",
                          borderRadius: "0.75rem",
                          background: "#FFD7D2",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          width="2.45rem"
                          height="2.45rem"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z"
                            fill="#000000"
                          />
                          <path
                            d="M4.5 20C4.5 15.8579 7.85786 12.5 12 12.5C16.1421 12.5 19.5 15.8579 19.5 20"
                            fill="#000000"
                          />
                        </svg>
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          right: "-1.15rem",
                          bottom: "-1.15rem",
                          width: "2.4rem",
                          height: "2.4rem",
                          borderRadius: "0.55rem",
                          background: "#FFFFFF",
                          border: "0.2rem solid #C41212",
                          color: "#C41212",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        ⊗
                      </div>
                    </div>

                    <div
                      style={{
                        color: "#C41212",
                        fontWeight: 700,
                        marginBottom: "1.8rem",
                        fontSize: "1.35rem"
                      }}
                    >
                      User does not exist
                    </div>
                    <div
                      style={{
                        color: "#374151",
                        fontSize: "1.25rem",
                        marginBottom: "0.5rem"
                      }}
                    >
                      We couldn't find a user with the email
                    </div>
                    <div
                      style={{
                        color: "#111827",
                        fontWeight: 700,
                        marginTop: "0.25rem",
                        marginBottom: "2.2rem",
                        fontSize: "1.2rem"
                      }}
                    >
                      {normalizeText(searchValue)}
                    </div>
                    <div
                      style={{
                        color: "#374151",
                        fontWeight: 700,
                        fontSize: "1.18rem"
                      }}
                    >
                      Please check the email and try again.
                    </div>
                  </div>
                </div>
              )}

              {!searching && selectedUser && (
                <>
                  <div
                    style={{
                      maxWidth: "40rem",
                      border: "1px solid #CBD5E1",
                      borderRadius: "0.55rem",
                      background: "#FFFFFF",
                      fontSize: "1rem",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background: "#F8FAFC",
                        borderBottom: "1px solid #CBD5E1",
                        padding: "0.65rem 1rem",
                        color: "#005EB8",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.55rem",
                      }}
                    >
                      <svg
                        width="1.35rem"
                        height="1.35rem"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        style={{ flex: "0 0 auto" }}
                      >
                        <circle cx="12" cy="12" r="10" fill="#005EB8" />
                        <circle cx="12" cy="9" r="3" fill="#FFFFFF" />
                        <path
                          d="M6.8 18.2C7.6 15.8 9.55 14.3 12 14.3C14.45 14.3 16.4 15.8 17.2 18.2"
                          stroke="#FFFFFF"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                        />
                      </svg>
                      User Details
                    </div>

                    <div style={{ padding: "1.25rem 1rem" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "1.25rem"
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "0.82rem",
                              textTransform: "uppercase",
                              color: "#374151"
                            }}
                          >
                            Name
                          </div>
                          <div
                            style={{
                              fontWeight: 700,
                              marginBottom: "1.25rem",
                              fontSize: "1rem"
                            }}
                          >
                            {getUserName(selectedUser) || "—"}
                          </div>

                          <div
                            style={{
                              fontSize: "0.82rem",
                              textTransform: "uppercase",
                              color: "#374151"
                            }}
                          >
                            Email
                          </div>
                          <div style={{ fontSize: "1rem" }}>
                            {getUserEmail(selectedUser) || "—"}
                          </div>
                        </div>

                        <div>
                          {latestActionResult ? (
                            <>
                              <div
                                style={{
                                  fontSize: "0.82rem",
                                  textTransform: "uppercase",
                                  color: "#374151"
                                }}
                              >
                                Recovery Status
                              </div>
                              <span
                                style={{
                                  display: "inline-block",
                                  background: "#DCFCE7",
                                  color: "#047857",
                                  borderRadius: "999px",
                                  padding: "0.2rem 0.65rem",
                                  fontWeight: 700,
                                  fontSize: "0.9rem",
                                  marginBottom: "1.25rem",
                                }}
                              >
                                ✓ Approved
                              </span>

                              <div
                                style={{
                                  fontSize: "0.82rem",
                                  textTransform: "uppercase",
                                  color: "#374151"
                                }}
                              >
                                Requested On
                              </div>
                              <div style={{ fontSize: "1rem" }}>
                                {formatDateTime(latestActionResult.at)}
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                style={{
                                  fontSize: "0.82rem",
                                  textTransform: "uppercase",
                                  color: "#374151"
                                }}
                              >
                                Recovery Status
                              </div>
                              <div
                                style={{
                                  color: "#6B7280",
                                  marginBottom: "1.25rem",
                                  fontSize: "1rem"
                                }}
                              >
                                —
                              </div>

                              <div
                                style={{
                                  fontSize: "0.82rem",
                                  textTransform: "uppercase",
                                  color: "#374151"
                                }}
                              >
                                Requested On
                              </div>
                              <div style={{ fontSize: "1rem" }}>—</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {!latestActionResult && (
                      <div
                        style={{
                          borderTop: "1px solid #CBD5E1",
                          background: "#F1F5F9",
                          padding: "0.85rem 1rem",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="primary"
                          className="btn"
                          style={{
                            color: "white",
                            border: "none",
                            borderRadius: "0.35rem",
                            minWidth: "13.5rem",
                            fontSize: "1rem",
                          }}
                          onClick={approveRecovery}
                          disabled={!selectedUser || submittingApprove}
                        >
                          🛡&nbsp; Approve Authenticator Reset{" "}
                          {submittingApprove && <InlineSpinner />}
                        </Button>
                      </div>
                    )}

                    {latestActionResult && totpResetUrl && (
                      <div
                        style={{
                          borderTop: "1px solid #CBD5E1",
                          padding: "1rem",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            marginBottom: "0.65rem"
                          }}
                        >
                          Authenticator Reset Link
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #CBD5E1",
                            background: "#EEF4FF",
                            padding: "0.6rem",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              color: "#005EB8",
                              fontSize: "0.95rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {totpResetUrl}
                          </div>

                          <Button
                            variant="light"
                            className="btn btn-sm"
                            style={{
                              border: "1px solid #CBD5E1",
                              background: "#FFFFFF",
                              fontSize: "0.9rem"
                            }}
                            onClick={copyTotpResetLink}
                          >
                            ⧉ Copy
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {!latestActionResult && (
                    <div
                      style={{
                        maxWidth: "40rem",
                        background: "#EAF2FF",
                        borderLeft: "0.25rem solid #005EB8",
                        padding: "1rem",
                        marginTop: "1.25rem",
                        color: "#1F2937",
                        fontSize: "0.95rem",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          marginBottom: "0.25rem",
                          fontSize: "1rem"
                        }}
                      >
                        ⓘ&nbsp;&nbsp;Security Policy Reminder
                      </div>
                      <div>
                        Approving an authenticator reset allows the user to
                        re-register their MFA credentials. Please verify the
                        user's identity via internal channels before
                        proceeding.
                      </div>
                    </div>
                  )}

                  {latestActionResult && totpResetUrl && (
                    <div
                      style={{
                        maxWidth: "40rem",
                        background: "#EAF2FF",
                        borderLeft: "0.25rem solid #005EB8",
                        padding: "1rem",
                        marginTop: "1.25rem",
                        color: "#1F2937",
                        fontSize: "0.95rem",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          marginBottom: "0.5rem",
                          fontSize: "1rem"
                        }}
                      >
                        ⓘ&nbsp;&nbsp;What happens next?
                      </div>
                      <div style={{ marginBottom: "0.35rem" }}>
                        A reset link has been generated for the user. The user
                        must use this link to reset and set up a new
                        authenticator app.
                      </div>
                      <div style={{ marginBottom: "0.35rem" }}>
                        This link is unique and can be used only once.
                      </div>
                      <div>
                        Once used, the user's previous authenticator-app
                        configuration will be replaced.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}